import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";
import type {
  BookingReportRow,
  PackageView,
  TravelBookingSettings,
  TripBookingView,
  TripSheetSyncView,
  TripSyncResult,
  TripView,
} from "../travel-types";

type TravelApiMethods = Pick<
  ApiClient,
  | "listTrips"
  | "listTripBookings"
  | "getTripBookingPassengers"
  | "moveTripBooking"
  | "requestTripDeposit"
  | "resolveTripDeposit"
  | "createTrip"
  | "updateTrip"
  | "deleteTrip"
  | "uploadTripBrochure"
  | "tripBrochureUrl"
  | "getTripSheetSync"
  | "saveTripSheetSync"
  | "syncTripSheetNow"
  | "listTravelPackages"
  | "createTravelPackage"
  | "updateTravelPackage"
  | "deleteTravelPackage"
  | "uploadPackagePhoto"
  | "deletePackagePhoto"
  | "packagePhotoUrl"
  | "getTravelBookingSettings"
  | "saveTravelBookingSettings"
  | "tripBookingsReport"
  | "tripBookingsCsvUrl"
  | "tripBookingAction"
>;

const reportQuery = (f?: { packageId?: string; tripId?: string }): string => {
  const q = new URLSearchParams();
  if (f?.packageId) q.set("packageId", f.packageId);
  if (f?.tripId) q.set("tripId", f.tripId);
  const text = q.toString();
  return text ? "?" + text : "";
};

export function createTravelApi({ base, request, ws }: HttpTransport): TravelApiMethods {
  return {
    listTrips: (id) => request<TripView[]>("GET", ws(id) + "/trips"),
    createTrip: (id, input) => request<TripView>("POST", ws(id) + "/trips", input),
    updateTrip: (id, tripId, input) =>
      request<TripView>("PATCH", ws(id) + "/trips/" + encodeURIComponent(tripId), input),
    deleteTrip: async (id, tripId) => {
      await request("DELETE", ws(id) + "/trips/" + encodeURIComponent(tripId));
    },
    uploadTripBrochure: async (id, tripId, file) => {
      await request("POST", ws(id) + "/trips/" + encodeURIComponent(tripId) + "/brochure", file);
    },
    tripBrochureUrl: (id, tripId) =>
      base + ws(id) + "/trips/" + encodeURIComponent(tripId) + "/brochure",
    getTripSheetSync: (id) => request<TripSheetSyncView | null>("GET", ws(id) + "/trip-sheet-sync"),
    saveTripSheetSync: (id, config) =>
      request<TripSheetSyncView>("PUT", ws(id) + "/trip-sheet-sync", config),
    syncTripSheetNow: (id) => request<TripSyncResult>("POST", ws(id) + "/trip-sheet-sync/sync", {}),
    listTravelPackages: (id) => request<PackageView[]>("GET", ws(id) + "/travel-packages"),
    createTravelPackage: (id, input) =>
      request<PackageView>("POST", ws(id) + "/travel-packages", input),
    updateTravelPackage: (id, packageId, input) =>
      request<PackageView>(
        "PATCH",
        ws(id) + "/travel-packages/" + encodeURIComponent(packageId),
        input,
      ),
    deleteTravelPackage: async (id, packageId) => {
      await request("DELETE", ws(id) + "/travel-packages/" + encodeURIComponent(packageId));
    },
    uploadPackagePhoto: async (id, packageId, file) => {
      await request(
        "POST",
        ws(id) + "/travel-packages/" + encodeURIComponent(packageId) + "/photos",
        file,
      );
    },
    deletePackagePhoto: async (id, packageId, photoId) => {
      await request(
        "DELETE",
        ws(id) + "/travel-packages/" + encodeURIComponent(packageId) + "/photos/" + photoId,
      );
    },
    packagePhotoUrl: (id, packageId, photoId) =>
      base + ws(id) + "/travel-packages/" + encodeURIComponent(packageId) + "/photos/" + photoId,
    getTravelBookingSettings: (id) =>
      request<TravelBookingSettings>("GET", ws(id) + "/travel/booking-settings"),
    saveTravelBookingSettings: (id, input) =>
      request<TravelBookingSettings>("PUT", ws(id) + "/travel/booking-settings", input),
    tripBookingsReport: (id, filter) =>
      request<BookingReportRow[]>("GET", ws(id) + "/trip-bookings-report" + reportQuery(filter)),
    tripBookingsCsvUrl: (id, filter) =>
      base + ws(id) + "/trip-bookings-report.csv" + reportQuery(filter),
    tripBookingAction: async (id, bookingId, action) => {
      await request(
        "POST",
        ws(id) + "/trip-bookings/" + encodeURIComponent(bookingId) + "/" + action,
        {},
      );
    },
    listTripBookings: (id) => request<TripBookingView[]>("GET", ws(id) + "/trip-bookings"),
    getTripBookingPassengers: async (id, bookingId) => {
      const root = ws(id) + "/trip-bookings/" + encodeURIComponent(bookingId);
      const [booking, docs] = await Promise.all([
        request<{ passengers?: { id: string; fullName: string; category: string }[] }>("GET", root),
        request<{ passengerId: string | null }[]>("GET", root + "/documents"),
      ]);
      return (booking.passengers ?? []).map((p) => ({
        id: p.id,
        fullName: p.fullName,
        category: p.category,
        documentCount: docs.filter((d) => d.passengerId === p.id).length,
      }));
    },
    requestTripDeposit: (id, bookingId) =>
      request<{ status: "pending_approval"; approvalId: string }>(
        "POST",
        ws(id) + "/trip-bookings/" + encodeURIComponent(bookingId) + "/deposit-request",
        {},
      ),
    resolveTripDeposit: (id, bookingId, approvalId) =>
      request<{ status: "approved" | "rejected"; booking: TripBookingView }>(
        "POST",
        ws(id) + "/trip-bookings/" + encodeURIComponent(bookingId) + "/deposit-resolve",
        { approvalId },
      ),
    moveTripBooking: (id, bookingId, to) =>
      request<TripBookingView>(
        "POST",
        ws(id) + "/trip-bookings/" + encodeURIComponent(bookingId) + "/status",
        { to },
      ),
  };
}
